import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Loader2, Moon } from "lucide-react";
import { API, fmtDate } from "./utils";
import { T } from "@/locales/tr";

const PURIFY_CONFIG = { ADD_ATTR: ["target", "rel"], FORBID_TAGS: ["script", "style", "iframe", "object", "embed"] };

export default function NewsletterTab({ pwd }) {
  const [editions, setEditions] = useState([]);
  const [subs, setSubs] = useState({ count: 0, subscribers: [] });
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(null);
  const [preview, setPreview] = useState(null);
  const [ramadan, setRamadan] = useState({ current_day: null, dispatches: [] });
  const [rDay, setRDay] = useState("");
  const [rSending, setRSending] = useState(false);

  const load = async () => {
    try {
      const [eds, ss, rm] = await Promise.all([
        axios.get(`${API}/admin/newsletter/editions`, { headers: { "X-Admin-Password": pwd } }),
        axios.get(`${API}/admin/newsletter/subscribers`, { headers: { "X-Admin-Password": pwd } }),
        axios.get(`${API}/admin/ramadan/dispatches`, { headers: { "X-Admin-Password": pwd } }),
      ]);
      setEditions(eds.data || []);
      setSubs(ss.data || { count: 0, subscribers: [] });
      setRamadan(rm.data || { current_day: null, dispatches: [] });
    } catch (e) { toast.error(T("Échec chargement newsletters")); }
  };
  useEffect(() => { load(); }, []);

  const triggerRamadan = async () => {
    if (!window.confirm(rDay ? `Envoyer le verset du jour ${rDay} à ${subs.count} abonnés ?` : `Envoyer le verset du jour Ramadan courant à ${subs.count} abonnés ?`)) return;
    setRSending(true);
    try {
      const url = rDay ? `${API}/admin/ramadan/dispatch?day=${rDay}` : `${API}/admin/ramadan/dispatch`;
      const { data } = await axios.post(url, null, { headers: { "X-Admin-Password": pwd }, timeout: 90000 });
      if (data.skipped) toast.info(`Skip : ${data.reason}`);
      else toast.success(`✅ Jour ${data.day} envoyé à ${data.sent} abonnés (${data.failed} échecs)`);
      setRDay("");
      load();
    } catch (e) { toast.error(T("Envoi Ramadan échoué")); }
    finally { setRSending(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/newsletter/generate`, { topic: topic || null }, { headers: { "X-Admin-Password": pwd }, timeout: 90000 });
      toast.success(T("Newsletter générée par Papicoo IA"));
      setTopic("");
      load();
      setPreview(data);
    } catch (e) { toast.error(T("Génération échouée")); }
    finally { setGenerating(false); }
  };

  const send = async (id) => {
    if (!window.confirm(`Envoyer à ${subs.count} abonnés ?`)) return;
    setSending(id);
    try {
      const { data } = await axios.post(`${API}/admin/newsletter/send`, { edition_id: id }, { headers: { "X-Admin-Password": pwd }, timeout: 120000 });
      toast.success(`✅ Envoyé à ${data.sent} abonnés (${data.failed} échecs)`);
      load();
    } catch (e) { toast.error(T("Envoi échoué")); }
    finally { setSending(null); }
  };

  return (
    <div data-testid="admin-newsletter-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-[color:var(--papicoo-ink)]">Newsletters · {subs.count} abonnés</h2>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)]">{T("↻ Rafraîchir")}</button>
      </div>
      <div className="papicoo-card p-5 mb-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-2">{T("Générer une nouvelle édition")}</div>
        <div className="flex gap-2 flex-col sm:flex-row">
          <input
            data-testid="newsletter-topic"
            placeholder={T("Sujet (laisse vide pour auto-tendances)…")}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="flex-1 px-3 py-2.5 rounded-lg border border-[color:var(--papicoo-border)] text-sm"
          />
          <button data-testid="newsletter-generate" onClick={generate} disabled={generating} className="px-4 py-2.5 rounded-lg papicoo-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {generating ? <Loader2 size={14} className="animate-spin" /> : "✨"} Générer
          </button>
        </div>
        <div className="text-[11px] italic text-[color:var(--papicoo-muted)] mt-2">
          {T("Papicoo écrira une newsletter de 350-450 mots structurée (Spirituel · Société · Action) au filtre coranique.")}
        </div>
      </div>

      {/* --- Ramadan auto-dispatch --- */}
      <div className="papicoo-card p-5 mb-5 bg-gradient-to-br from-emerald-50 to-amber-50 border-l-4 border-[color:var(--papicoo-gold)]">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-2">
          <Moon size={14} /> {T("Ramadan 1447H · Envoi automatique")}
        </div>
        <p className="text-sm text-[color:var(--papicoo-muted)] mb-3 leading-relaxed">
          {ramadan.current_day
            ? <>{T("Aujourd'hui :")} <strong>jour {ramadan.current_day} de Ramadan</strong> {T("— l'envoi 06h00 Paris est programmé.")}</>
            : <>{T("Hors période Ramadan (début prévu :")} <strong>{ramadan.start_date}</strong>{T("). Pendant le mois béni, Papicoo enverra automatiquement le verset du jour à")} <strong>{subs.count} abonné(s)</strong> {T("chaque matin à 06h00 Paris.")}</>}
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            data-testid="ramadan-day-input"
            type="number"
            min="1" max="30"
            value={rDay}
            onChange={(e) => setRDay(e.target.value)}
            placeholder="Jour (1-30, vide = auto)"
            className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm w-44"
          />
          <button
            data-testid="ramadan-trigger"
            onClick={triggerRamadan}
            disabled={rSending}
            className="px-4 py-2 rounded-full papicoo-btn-gold text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            {rSending ? <Loader2 size={14} className="animate-spin" /> : <Moon size={14} />}
            {rSending ? "Envoi…" : "Tester l'envoi maintenant"}
          </button>
          <span className="text-[11px] text-[color:var(--papicoo-muted)] italic ml-auto">
            Historique : {ramadan.dispatches?.length || 0} envoi(s)
          </span>
        </div>
        {ramadan.dispatches && ramadan.dispatches.length > 0 && (
          <div className="mt-3 text-[11px] text-[color:var(--papicoo-muted)] grid grid-cols-2 sm:grid-cols-3 gap-1">
            {ramadan.dispatches.slice(0, 6).map((d) => (
              <div key={`day-${d.day}-${d.year || ""}`} className="flex justify-between border-t border-[color:var(--papicoo-border)] pt-1">
                <span>Jour {d.day}</span>
                <span className={d.sent > 0 ? "text-emerald-700" : "text-amber-700"}>{d.sent}✓ / {d.failed}✗</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="papicoo-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--papicoo-sand-deep)]/40 text-[color:var(--papicoo-muted)] text-[11px] uppercase tracking-[0.15em]">
            <tr><th className="text-left p-3">{T("Titre")}</th><th className="text-left p-3">{T("Statut")}</th><th className="text-left p-3">{T("Envoyée")}</th><th className="text-left p-3">{T("Date")}</th><th className="text-left p-3">{T("Actions")}</th></tr>
          </thead>
          <tbody>
            {editions.length === 0 ? (
              <tr><td colSpan={5} className="text-center italic text-[color:var(--papicoo-muted)] py-8">{T("Aucune édition. Génère-en une ci-dessus.")}</td></tr>
            ) : editions.map((e) => (
              <tr key={e.id} className="border-t border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/20">
                <td className="p-3 max-w-md truncate font-medium" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(e.title || "", PURIFY_CONFIG)}} />
                <td className="p-3"><span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${e.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{e.status}</span></td>
                <td className="p-3 text-xs">{e.sent_to || 0}</td>
                <td className="p-3 text-[11px] text-[color:var(--papicoo-muted)]">{fmtDate(e.created_at)}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setPreview(e)} className="text-xs underline">{T("Aperçu")}</button>
                  {e.status !== "sent" && (
                    <button data-testid={`send-${e.id}`} onClick={() => send(e.id)} disabled={sending === e.id} className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      {sending === e.id ? <Loader2 size={11} className="animate-spin" /> : "✉️"} Envoyer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="papicoo-card p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-accent)] mb-3">{T("Aperçu newsletter")}</div>
            <div className="prose max-w-none" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(preview.html || "", PURIFY_CONFIG)}} />
            <button onClick={() => setPreview(null)} className="mt-4 text-xs text-[color:var(--papicoo-muted)] underline">{T("Fermer")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
